/**
 * Formulaire de configuration 2FA par Email
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Formulaire pour configurer l'authentification 2FA par email.
 */

 // Client Component pour Next.js

import { useState, useEffect, useRef } from 'react';
import { use2FA } from '../../hooks';
import { BackupCodesDisplay } from '../ui/BackupCodesDisplay';
import { ButtonSpinner } from '../ui/LoadingSpinner';

/**
 * Props du composant EmailSetupForm
 */
export interface EmailSetupFormProps {
  /** ID de l'utilisateur */
  userId: string;
  /** Callback après activation réussie */
  onSuccess?: (backupCodes: string[]) => void;
  /** Callback en cas d'erreur */
  onError?: (error: string) => void;
  /** Callback pour annuler */
  onCancel?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Étapes du formulaire
 */
type SetupStep = 'send' | 'verify' | 'backup';

/**
 * Composant EmailSetupForm
 */
export function EmailSetupForm({
  userId,
  onSuccess,
  onError,
  onCancel,
  className = '',
}: EmailSetupFormProps) {
  const {
    sendEmailCode,
    activate2FA,
    isLoading,
    error,
    backupCodes,
    clearError,
    clearData,
  } = use2FA();

  const [step, setStep] = useState<SetupStep>('send');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);

  // ✅ FIX: Utiliser useRef au lieu de state pour résister au StrictMode
  const sendAttemptedRef = useRef(false);

  // Envoyer le code automatiquement au montage (une seule fois, même en StrictMode)
  useEffect(() => {
    if (step === 'send' && !codeSent && !sendAttemptedRef.current) {
      sendAttemptedRef.current = true; // Marquer tentative AVANT l'appel (persiste entre re-renders)
      handleSendCode();
    }
  }, [step, codeSent]); // sendAttemptedRef pas dans les dépendances car c'est une ref

  /**
   * Envoyer le code par email
   */
  const handleSendCode = async () => {
    clearError();
    try {
      await sendEmailCode();
      setCodeSent(true);
      setStep('verify');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur d\'envoi du code';
      onError?.(message);
    }
  };

  /**
   * Vérifier le code et activer 2FA
   */
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setCodeError(null);

    // Validation du code
    if (!verificationCode) {
      setCodeError('Le code est requis');
      return;
    }

    if (!/^\d{6}$/.test(verificationCode)) {
      setCodeError('Le code doit contenir 6 chiffres');
      return;
    }

    try {
      const codes = await activate2FA('EMAIL', verificationCode);

      if (codes && codes.length > 0) {
        setStep('backup');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Code invalide';
      setCodeError(message);
      onError?.(message);
    }
  };

  /**
   * Finaliser la configuration
   */
  const handleFinish = () => {
    onSuccess?.(backupCodes);
    clearData();
  };

  /**
   * Annuler la configuration
   */
  const handleCancel = () => {
    clearData();
    setVerificationCode('');
    setCodeSent(false);
    setStep('send');
    onCancel?.();
  };

  /**
   * Renvoyer le code
   */
  const handleResendCode = async () => {
    setVerificationCode('');
    setCodeError(null);
    await handleSendCode();
  };

  return (
    <div className={`w-full max-w-2xl mx-auto ${className}`}>
      {/* Étape 1: Envoi du code (avec loader) */}
      {step === 'send' && (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-accent-light rounded-full flex items-center justify-center mb-4 animate-pulse">
            <svg
              className="w-8 h-8 text-accent"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-content mb-2">
            Envoi du code en cours...
          </h3>
          <p className="text-sm text-content-muted">
            Un code de vérification est en cours d'envoi à votre adresse email
          </p>
        </div>
      )}

      {/* Étape 2: Vérification du code */}
      {step === 'verify' && (
        <div className="space-y-6">
          {/* Message de succès d'envoi */}
          <div className="bg-success-light border border-success rounded-lg p-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-success mt-0.5 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-success">
                  Code envoyé !
                </h4>
                <p className="mt-1 text-sm text-success">
                  Un code de vérification à 6 chiffres a été envoyé à votre adresse email.
                  Vérifiez votre boîte de réception (et vos spams).
                </p>
              </div>
            </div>
          </div>

          {/* Formulaire de saisie du code */}
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label
                htmlFor="verification-code"
                className="block text-sm font-medium text-content-muted mb-2"
              >
                Code de vérification
              </label>
              <input
                id="verification-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value.replace(/\D/g, ''));
                  setCodeError(null);
                }}
                placeholder="000000"
                className="w-full px-4 py-3 text-center text-2xl tracking-widest font-mono border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-surface text-content"
                disabled={isLoading}
                autoFocus
              />
              {codeError && (
                <p className="mt-2 text-sm text-danger">
                  {codeError}
                </p>
              )}
            </div>

            {/* Erreur globale */}
            {error && (
              <div className="p-3 bg-danger-light border border-danger rounded-lg">
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}

            {/* Boutons d'action */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isLoading || verificationCode.length !== 6}
                className="flex-1 flex items-center justify-center px-4 py-3 bg-primary hover:bg-primary-hover text-content-inverted font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <ButtonSpinner />
                    Vérification...
                  </>
                ) : (
                  'Vérifier et activer'
                )}
              </button>

              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="px-4 py-3 border border-border text-content-muted hover:bg-surface-raised font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
            </div>
          </form>

          {/* Lien pour renvoyer le code */}
          <div className="text-center">
            <button
              onClick={handleResendCode}
              disabled={isLoading}
              className="text-sm text-primary hover:text-primary-hover disabled:opacity-50"
            >
              Renvoyer le code
            </button>
          </div>
        </div>
      )}

      {/* Étape 3: Affichage des codes de backup */}
      {step === 'backup' && backupCodes.length > 0 && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="mx-auto w-12 h-12 bg-success-light rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-content">
              Authentification par email activée !
            </h3>
            <p className="mt-2 text-sm text-content-muted">
              Voici vos codes de backup. Conservez-les en lieu sûr.
            </p>
          </div>

          <BackupCodesDisplay codes={backupCodes} />

          <button
            onClick={handleFinish}
            className="w-full px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-content-inverted bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Terminer
          </button>
        </div>
      )}
    </div>
  );
}
